// What changed in a submission, as MongoDB dotted paths
// Covers empty to filled, filled to changed, and filled to cleared
// A clear is an empty value in `set`, or a path in `unset` if the key is gone

export interface FormDelta {
  set: Record<string, any>;
  unset: string[];
}

// Fields an autosave must never write, kept in sync with AutosaveAPI.js
export const PROTECTED_PATHS: string[] = [
  'data.localStatus',
  'data.lockedBy',
  'data.lockDateTime',
  'data.updatedBy',
  'data.role',
  'data.selectChiroCenter',
  'data.patientEmail',
  'data.dynamicEmail',
  'data.salesforceAccountId',
  'data.patientInformedConsentForm',
  'data.agreementsAuthorizations',
  'data.submit',
];

// A bigger delta than this means a bad baseline, not that much typing
export const MAX_DELTA_PATHS = 400;

const isPlainObject = (value: any): boolean =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date);

const isProtectedPath = (path: string): boolean =>
  PROTECTED_PATHS.some((protectedPath) => path === protectedPath || path.startsWith(`${protectedPath}.`));

// Keys unsafe in a dotted path, or reaching a prototype
const isSafeKey = (key: string): boolean =>
  key.length > 0 &&
  !key.includes('.') &&
  !key.startsWith('$') &&
  key !== '__proto__' &&
  key !== 'prototype' &&
  key !== 'constructor';

// Puts both sides of a compare in the shape the request carries
// Without it a Date and its ISO string look like a change
export const normalizeFormData = (data: any): any => {
  if (data === null || data === undefined) return {};
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    // Won't serialize, won't autosave, so treat as empty
    return {};
  }
};

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === null || b === null || a === undefined || b === undefined) return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(
      (key) => Object.prototype.hasOwnProperty.call(b, key) && deepEqual(a[key], b[key])
    );
  }

  return false;
};

const walk = (
  baseline: any,
  current: any,
  prefix: string,
  set: Record<string, any>,
  unset: string[]
): void => {
  const keys = new Set<string>([
    ...Object.keys(baseline ?? {}),
    ...Object.keys(current ?? {}),
  ]);

  for (const key of keys) {
    if (!isSafeKey(key)) continue;

    const path = `${prefix}.${key}`;
    if (isProtectedPath(path)) continue;

    const baseValue = baseline?.[key];
    const currentValue = current?.[key];

    // Key is gone from the submission, so clear it in the DB
    if (currentValue === undefined) {
      if (baseValue !== undefined) unset.push(path);
      continue;
    }

    // Two objects, descend so only changed leaves are written
    if (isPlainObject(baseValue) && isPlainObject(currentValue)) {
      walk(baseValue, currentValue, path, set, unset);
      continue;
    }

    // Arrays written whole, never per index, a removed row shifts the rest
    if (!deepEqual(baseValue, currentValue)) {
      set[path] = currentValue;
    }
  }
};

// Diffs the baseline against the form's current data
export const computeFormDelta = (baseline: any, current: any): FormDelta => {
  const set: Record<string, any> = {};
  const unset: string[] = [];

  walk(normalizeFormData(baseline), normalizeFormData(current), 'data', set, unset);

  return { set, unset };
};

export const isEmptyDelta = (delta: FormDelta): boolean =>
  Object.keys(delta.set).length === 0 && delta.unset.length === 0;

export const countDeltaPaths = (delta: FormDelta): number =>
  Object.keys(delta.set).length + delta.unset.length;
